import { Activity, ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ThreadTodoItem, ThreadTodoStatus } from "@/api/types";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  INITIAL_TEAM_DIAGRAM_STATE,
  TeamDiagram,
  teamDiagramReducer,
} from "@/features/team-sessions/components/TeamDiagram";
import { useTeamSession } from "@/features/team-sessions/hooks/useTeamSessions";
import { ChatSidePanel } from "@/features/threads/components/ChatSidePanel";
import { ChatStream } from "@/features/threads/components/ChatStream";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/uiStore";

interface TeamChatViewProps {
  /** Sessão de time que define os subagentes/supervisor (para o diagrama). */
  sessionId: string;
  /** Thread existente a abrir (histórico). Ausente => modo rascunho. */
  threadId?: string;
  /** Rota do botão voltar. */
  backTo: string;
}

/**
 * Layout de conversa de TIME (diagrama supervisor+subagentes, TODOs, eventos
 * de delegação). Usado tanto ao iniciar uma sessão (modo rascunho, sem
 * ``threadId``) quanto ao abrir uma conversa de time existente pela tela de
 * Conversas (com ``threadId`` -> carrega o histórico). Antes, abrir uma
 * conversa de time caía no layout 1:1; agora o ChatPage delega para cá.
 */
export function TeamChatView({
  sessionId,
  threadId: initialThreadId,
  backTo,
}: TeamChatViewProps) {
  const { t } = useTranslation("threads");
  const navigate = useNavigate();
  const detail = useTeamSession(sessionId);
  // Estado do diagrama derivado do FLUXO de eventos via reducer puro. NAO
  // usamos "ultimo evento" (last-write-wins perde delegacoes paralelas que
  // chegam no mesmo tick) -- ver nota em ``teamDiagramReducer``.
  const [diagramState, setDiagramState] = useState(
    INITIAL_TEAM_DIAGRAM_STATE,
  );
  const [streaming, setStreaming] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const [liveTodos, setLiveTodos] = useState<ThreadTodoItem[] | undefined>(
    undefined,
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const sidePanelCollapsed = useUiStore((s) => s.chatSidePanelCollapsed);
  const toggleSidePanel = useUiStore((s) => s.toggleChatSidePanel);

  const supervisorSlug = useMemo(() => {
    if (!detail.data) return null;
    const supId = detail.data.supervisor_agent_id;
    if (!supId) return null;
    return detail.data.agents.find((a) => a.agent_id === supId)?.slug ?? null;
  }, [detail.data]);

  if (detail.isLoading) return <LoadingState />;
  if (detail.isError) return <ErrorState error={detail.error} />;
  if (!detail.data) return null;

  function handleSseEvent(ev: { event: string; data: unknown }) {
    // Acumula o estado do diagrama por evento (functional update = batch-safe:
    // todas as delegacoes paralelas do mesmo tick sao aplicadas em ordem).
    setDiagramState((prev) => teamDiagramReducer(prev, ev));
    if (ev.event === "agent_started") setStreaming(true);
    if (
      ev.event === "agent_completed" ||
      ev.event === "message_completed" ||
      ev.event === "error" ||
      ev.event === "cancelled" ||
      ev.event === "done"
    ) {
      setStreaming(false);
    }
    if (ev.event === "done") {
      setRefreshKey((k) => k + 1);
    }
    if (ev.event === "todos_updated") {
      const data = ev.data as { todos?: unknown };
      const rawTodos = Array.isArray(data?.todos) ? data.todos : [];
      const normalized: ThreadTodoItem[] = rawTodos.map((item) => {
        const dict =
          typeof item === "object" && item !== null
            ? (item as Record<string, unknown>)
            : {};
        const content = typeof dict.content === "string" ? dict.content : "";
        const status: ThreadTodoStatus =
          dict.status === "completed" ||
          dict.status === "in_progress" ||
          dict.status === "pending"
            ? dict.status
            : "pending";
        return { content, status };
      });
      setLiveTodos(normalized);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <header className="flex items-center justify-between border-b border-line pb-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backTo)}
            aria-label={t("backToList")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold text-ink-950">
              {detail.data.name}
            </h1>
            <div className="flex flex-wrap gap-1">
              {detail.data.agents.map((a) => (
                <Badge key={a.agent_id} variant="outline" className="text-xs">
                  {a.slug}
                </Badge>
              ))}
              <Badge variant="secondary" className="text-xs">
                max {detail.data.max_delegations} delegações
              </Badge>
            </div>
          </div>
        </div>
        <Sheet open={sidePanelOpen} onOpenChange={setSidePanelOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={t("sidePanel.toggle")}
            >
              <Activity className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[320px] sm:max-w-sm">
            <SheetHeader>
              <SheetTitle>{t("sidePanel.title")}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 h-full">
              <ChatSidePanel
                threadId={threadId ?? undefined}
                isTeamSession
                streaming={streaming}
                liveTodos={liveTodos}
                refreshKey={refreshKey}
              />
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <div className="flex min-h-0 flex-1 gap-3">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <div className="shrink-0">
            <TeamDiagram
              subagents={detail.data.agents.map((a) => ({
                agent_id: a.agent_id,
                slug: a.slug,
                name: a.name,
              }))}
              supervisorSlug={supervisorSlug}
              supervisorState={diagramState.supervisorState}
              activeSlugs={diagramState.activeSlugs}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            <ChatStream
              threadId={threadId ?? ""}
              draftSessionId={threadId ? undefined : sessionId}
              onThreadCreated={(id) => setThreadId(id)}
              onSseEvent={handleSseEvent}
            />
          </div>
        </div>
        <ChatSidePanel
          threadId={threadId ?? undefined}
          isTeamSession
          streaming={streaming}
          liveTodos={liveTodos}
          refreshKey={refreshKey}
          collapsed={sidePanelCollapsed}
          onToggleCollapsed={toggleSidePanel}
          className={cn(
            "hidden shrink-0 lg:flex",
            sidePanelCollapsed ? "lg:w-[56px]" : "lg:w-[320px] xl:w-[360px]",
          )}
        />
      </div>
    </div>
  );
}
